import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

export default function RoleExperience() {
  const navigate = useNavigate();
  const [activeRole, setActiveRole] = useState('admin');

  const roles = [
    {
      id: 'admin',
      label: 'Admin Workspace',
      badge: 'Full School Control',
      color: 'from-blue-600 to-indigo-600',
      accent: 'border-blue-500 text-blue-400',
      desc: 'Complete administrative oversight. Manage user accounts, link parent-student relationships, view school analytics, configure timetables, and audit grade books.',
      highlights: [
        'Centralized User & Role Management',
        'School-wide Analytics & KPI Dashboards',
        'Parent-Student Linkage Engine',
        'Global Timetable & Class Configuration',
      ],
      previewStats: [
        { label: 'Enrolled Users', val: '1,420' },
        { label: 'Active Classes', val: '48' },
        { label: 'Global Attendance', val: '94.8%' },
      ],
      action: 'Explore Admin Role',
    },
    {
      id: 'teacher',
      label: 'Teacher Portal',
      badge: 'Classroom Efficiency',
      color: 'from-emerald-600 to-teal-600',
      accent: 'border-emerald-500 text-emerald-400',
      desc: 'Designed for daily classroom operations. Take morning attendance, create auto-graded MCQ quizzes, collect assignments, schedule timetables, and broadcast class notifications.',
      highlights: [
        '1-Click Attendance Roll Call',
        'Quiz Builder with Custom Timers',
        'Assignment Grading & Feedback',
        'Classroom Timetable & Notifications',
      ],
      previewStats: [
        { label: 'My Classes', val: '5 Sections' },
        { label: 'Quizzes Built', val: '14 Active' },
        { label: 'Submissions Scored', val: '182 Files' },
      ],
      action: 'Explore Teacher Role',
    },
    {
      id: 'student',
      label: 'Student Hub',
      badge: 'Interactive Learning',
      color: 'from-purple-600 to-pink-600',
      accent: 'border-purple-500 text-purple-400',
      desc: 'An empowering student dashboard. Take timed online quizzes, upload homework assignments, check weekly class schedules, and track academic grade averages.',
      highlights: [
        'Interactive Timed Quiz Engine',
        'Digital Homework Upload & Status',
        'Personal Timetable & Grade View',
        'Real-time Notification Bell',
      ],
      previewStats: [
        { label: 'Current GPA', val: '3.92' },
        { label: 'Quizzes Passed', val: '22 / 24' },
        { label: 'Pending Due', val: '2 Work' },
      ],
      action: 'Explore Student Role',
    },
    {
      id: 'parent',
      label: 'Parent Portal',
      badge: 'Academic Transparency',
      color: 'from-amber-600 to-orange-600',
      accent: 'border-amber-500 text-amber-400',
      desc: 'Complete peace of mind for parents. Track your child’s daily attendance, review quiz results, monitor homework completion, message teachers, and download PDF report cards.',
      highlights: [
        'Child Attendance Calendar View',
        'Real-time Quiz & Test Results',
        'Teacher Direct Messaging',
        '1-Click PDF Report Card Export',
      ],
      previewStats: [
        { label: 'Child Attendance', val: '96.4%' },
        { label: 'Latest Assessment', val: '92/100' },
        { label: 'PDF Reports', val: 'Ready' },
      ],
      action: 'Explore Parent Role',
    },
  ];

  const current = roles.find((r) => r.id === activeRole) || roles[0];

  return (
    <section className="py-24 bg-slate-950 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="text-xs font-bold uppercase tracking-widest text-blue-400 mb-3">
            ROLE-BASED WORKSPACES
          </div>
          <h2 className="font-serif text-3xl sm:text-5xl font-extrabold text-white tracking-tight mb-4">
            One Platform. Four Experiences.
          </h2>
          <p className="text-slate-400 text-base sm:text-lg">
            Tailored interfaces engineered specifically for each stakeholder in the educational journey.
          </p>
        </div>

        {/* Tab Buttons */}
        <div className="flex flex-wrap items-center justify-center gap-3 mb-12">
          {roles.map((r) => (
            <button
              key={r.id}
              onClick={() => setActiveRole(r.id)}
              className={`px-6 py-3 rounded-2xl text-sm font-bold transition-all duration-300 flex items-center gap-2 ${
                activeRole === r.id
                  ? `bg-gradient-to-r ${r.color} text-white shadow-lg shadow-black/40 scale-105`
                  : 'bg-white/5 text-slate-400 hover:text-white border border-white/10 hover:bg-white/10'
              }`}
            >
              <span>{r.label}</span>
            </button>
          ))}
        </div>

        {/* Active Role Content Card */}
        <div className="bg-slate-900/80 border border-white/15 rounded-3xl p-6 md:p-10 shadow-2xl backdrop-blur-xl grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          {/* Left Column: Role Details */}
          <div className="lg:col-span-6 space-y-6 text-left">
            <div className="inline-block px-3 py-1 rounded-full text-xs font-extrabold uppercase tracking-wider bg-white/5 border border-white/10 text-slate-300">
              {current.badge}
            </div>

            <h3 className="text-3xl font-extrabold text-white">
              {current.label}
            </h3>

            <p className="text-slate-300 text-base leading-relaxed">
              {current.desc}
            </p>

            {/* Key Capabilities List */}
            <div className="space-y-3 pt-2">
              <div className="text-xs font-bold uppercase tracking-wider text-slate-400">
                Core Capabilities:
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {current.highlights.map((h, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs text-slate-200 font-medium bg-white/5 p-2.5 rounded-xl border border-white/5">
                    <span className="text-blue-400 font-bold">✓</span> {h}
                  </div>
                ))}
              </div>
            </div>

            {/* Action Button */}
            <div className="pt-4">
              <button
                onClick={() => navigate('/select-role')}
                className={`px-6 py-3 rounded-xl font-bold text-sm text-white bg-gradient-to-r ${current.color} shadow-lg hover:brightness-110 transition-all`}
              >
                Access {current.label} Demo →
              </button>
            </div>
          </div>

          {/* Right Column: Simulated Workspace Card */}
          <div className="lg:col-span-6">
            <div className="bg-slate-950 border border-white/10 rounded-2xl p-6 space-y-6 shadow-xl">
              <div className="flex items-center justify-between border-b border-white/10 pb-4">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl bg-gradient-to-tr ${current.color} flex items-center justify-center font-bold text-white text-sm shadow-md`}>
                    EP
                  </div>
                  <div>
                    <div className="text-xs text-slate-400 font-medium">Logged in as</div>
                    <div className="text-sm font-bold text-white">{current.label} User</div>
                  </div>
                </div>
                <span className="text-xs font-bold text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-2.5 py-1 rounded-full">
                  Authenticated
                </span>
              </div>

              {/* Stats Bar */}
              <div className="grid grid-cols-3 gap-3">
                {current.previewStats.map((st, i) => (
                  <div key={i} className="bg-white/5 border border-white/5 rounded-xl p-3 text-center">
                    <div className="text-[11px] text-slate-400 font-medium">{st.label}</div>
                    <div className="text-lg font-extrabold text-white mt-0.5">{st.val}</div>
                  </div>
                ))}
              </div>

              {/* Workflow Status Box */}
              <div className="bg-white/5 border border-white/5 rounded-xl p-4 space-y-2">
                <div className="flex justify-between items-center text-xs text-slate-300 font-semibold">
                  <span>Role Permissions & Workflow</span>
                  <span className="text-blue-400">100% Configured</span>
                </div>
                <div className="w-full bg-slate-800 h-2 rounded-full overflow-hidden">
                  <div className={`h-full bg-gradient-to-r ${current.color} w-full`} />
                </div>
                <p className="text-[11px] text-slate-400 pt-1">
                  Role-Based Access Control (RBAC) enforces strict data isolation and security boundaries.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
